import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ReferenceType } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/request/GetReferencesStockRequest';
import { RootState } from 'app/AppStore';
import { TagsIcon } from 'assets/icons';
import { calculatePriceAfterDiscount, getDiscountColor, getDiscountTextStyle } from 'components/Discount';
import Popover from 'components/Popover';
import ClientDiscountView from 'components/ReferencePriceSection/ClientDiscountView';
import {
  getDiscountedPrice,
  getDiscountText,
  getVatExcludedText,
} from 'components/ReferencePriceSection/ReferencePriceSectionUtils';
import { getDiscount, getPriceMapValue, getPrices } from 'domains/references';
import { ClientView, GarageView, SparePartsViewType } from 'domains/user';
import { theme } from 'styles';
import { Flex, Icon, MarginBox, Text } from 'UI';
import { getData, textFormatter } from 'utils';
import { ExtendedDiscountPriceRow, PclPriceWrapper, SFlex } from './ReferencePriceSection.styled';

const PRICE_VAT_EXCL_DATA_CY = 'price-vat-excl';

interface PriceSectionProps {
  referenceNumber: string;
  referenceType: ReferenceType;
  sparePartsView?: SparePartsViewType;
  isMKTP?: boolean;
  align?: 'left' | 'right';
  hideDiscountSection?: boolean;
  narrow?: boolean;
  useCompactView?: boolean;
  clientDiscount?: number;
  isAvailableSoon?: boolean;
  isNoLongerAvailable?: boolean;
  sellerNumber?: string;
  extendedDiscountView?: boolean;
}

const PriceSection = ({
  referenceNumber,
  referenceType,
  isMKTP = false,
  hideDiscountSection,
  clientDiscount,
  isAvailableSoon,
  isNoLongerAvailable,
  sparePartsView = ClientView,
  useCompactView = false,
  narrow,
  sellerNumber,
  extendedDiscountView = false,
  align = 'right',
}: PriceSectionProps) => {
  const { t } = useTranslation();
  const pricesMap = useSelector((state: RootState) => getPrices(state, referenceNumber));
  const referenceDiscount = useSelector((state: RootState) => getDiscount(state, referenceNumber));
  const [displayDetailedView, setDisplayDetailedView] = useState<boolean>(false);
  const isTire = referenceType === ('TIRE' as ReferenceType);

  const priceData = getData(getPriceMapValue(pricesMap, sellerNumber));
  const doesRefHaveMultiplePrices = pricesMap instanceof Map ? pricesMap.size > 1 && !sellerNumber : false;

  const garagePriceVatExcluded = calculatePriceAfterDiscount(referenceDiscount, priceData, false);
  const recommendedPriceVatExcluded = Number(priceData?.clientView?.recommendedPriceVatExcluded);
  const clientPriceVatExcluded = getDiscountedPrice(recommendedPriceVatExcluded, clientDiscount);
  const recommendedPriceVatIncluded = Number(priceData?.clientView?.recommendedPriceVatIncluded);
  const clientPriceVatIncluded = getDiscountedPrice(recommendedPriceVatIncluded, clientDiscount);
  const vatExcludedMargin = clientPriceVatExcluded - garagePriceVatExcluded;

  const renderGaragePrice = useCallback(() => {
    if (!priceData) {
      return <></>;
    }

    const renderVatIncludedPrice = () =>
      !(isMKTP && sparePartsView === GarageView) && (
        <Text type={'light_12_black_65'} data-cy={'price-vat-incl'}>
          {t('common.price.or_vat_include_price', 'or {{vat_include_price}} VAT. Incl', {
            vat_include_price: textFormatter.formatCurrency(
              calculatePriceAfterDiscount(referenceDiscount, priceData, true),
              priceData.currency,
            ),
          })}
        </Text>
      );

    const renderPrice = () => {
      const priceWithoutPromotion = getVatExcludedText(
        t,
        Number(priceData.garageView?.vatExcludedPrice),
        priceData?.currency,
      );

      if (extendedDiscountView && referenceDiscount) {
        return (
          <Flex direction={'column'}>
            <ExtendedDiscountPriceRow align={'center'} gap={10} data-cy={'extended-discount-price-row'}>
              <Text
                type={'h5_bold'}
                displayStyle={getDiscountTextStyle(referenceDiscount)}
                data-cy={PRICE_VAT_EXCL_DATA_CY}
              >
                {getVatExcludedText(t, garagePriceVatExcluded, priceData?.currency, doesRefHaveMultiplePrices)}
              </Text>
              <Text type={'light_12_black_65'} decoration={'line-through'} data-cy={'previous-price-vat-excl'}>
                {priceWithoutPromotion}
              </Text>
              <Flex align={'center'} gap={3} size={0} data-cy={'extended-discount-percent'}>
                <Icon IconComponent={TagsIcon} size={16} color={getDiscountColor(referenceDiscount)} noPointer />
                <Text type={'light_12_black_65'} disableGutter>
                  {`${referenceDiscount.discount}%`}
                </Text>
              </Flex>
            </ExtendedDiscountPriceRow>
            {renderVatIncludedPrice()}
          </Flex>
        );
      }

      return (
        <Flex direction={'column'}>
          <Text
            type={'h5_bold'}
            displayStyle={getDiscountTextStyle(referenceDiscount)}
            data-cy={PRICE_VAT_EXCL_DATA_CY}
          >
            {getVatExcludedText(t, garagePriceVatExcluded, priceData?.currency, doesRefHaveMultiplePrices)}
          </Text>
          {referenceDiscount && (
            <Text type={'light_12_black_65'} decoration={'line-through'}>
              {priceWithoutPromotion}
            </Text>
          )}
          {renderVatIncludedPrice()}
        </Flex>
      );
    };
    if (useCompactView) {
      return (
        <Popover
          content={
            <SFlex direction={'column'}>
              <Text type="sm">
                {t('common.price.vat_exclude_margin', '{{vat_exclude_margin}} VAT. Excl Margin', {
                  vat_exclude_margin: textFormatter.formatCurrency(vatExcludedMargin, priceData.currency),
                })}
              </Text>
              <Text type="sm">{getDiscountText(t, priceData.garageView?.discountRate)}</Text>
              {referenceDiscount && (
                <Text type="sm" displayStyle={getDiscountTextStyle(referenceDiscount)}>
                  {`${referenceDiscount.discount}% ${t('common.discount.additional_discount', 'Additional discount')}`}
                </Text>
              )}
            </SFlex>
          }
          arrowColor={theme.color.white}
          placement={'top'}
        >
          {renderPrice()}
        </Popover>
      );
    } else {
      return renderPrice();
    }
  }, [
    garagePriceVatExcluded,
    isMKTP,
    sparePartsView,
    extendedDiscountView,
    priceData,
    referenceDiscount,
    t,
    useCompactView,
    vatExcludedMargin,
    doesRefHaveMultiplePrices,
  ]);

  const renderClientPrice = useCallback(() => {
    if (!priceData) {
      return <></>;
    }

    const renderPclPrice = () => (
      <PclPriceWrapper direction={'column'} background={theme.color.grey90}>
        <Text type="sm" color="grey35">
          {t('common.price.pcl', 'PCL')}
        </Text>
        <Text type="base" bold data-cy={'price-vat-incl'}>
          {getVatExcludedText(t, recommendedPriceVatExcluded, priceData?.currency)}
        </Text>
        <Text type="sm" color="grey35" data-cy={PRICE_VAT_EXCL_DATA_CY}>
          {t('common.price.or_vat_include_price', 'or {{vat_include_price}} VAT. Incl', {
            vat_include_price: textFormatter.formatCurrency(recommendedPriceVatIncluded, priceData?.currency ?? ''),
          })}
        </Text>
      </PclPriceWrapper>
    );

    const renderPrice = () => {
      const isRecommendedPclPriceEqualPclPrice = recommendedPriceVatExcluded === clientPriceVatExcluded;
      return (
        <Flex direction={'column'}>
          {isTire && sparePartsView === ClientView && (
            <>
              {!isRecommendedPclPriceEqualPclPrice && renderPclPrice()}
              <Text type="sm">
                {isRecommendedPclPriceEqualPclPrice
                  ? t('common.price.pcl', 'PCL')
                  : t('common.price.recommended_pcl', 'Recommended PCL')}
              </Text>
            </>
          )}
          <Text type={'h5_bold'} data-cy={PRICE_VAT_EXCL_DATA_CY}>
            {getVatExcludedText(t, clientPriceVatExcluded, priceData?.currency, doesRefHaveMultiplePrices)}
          </Text>
          {!(isMKTP && sparePartsView === GarageView) && (
            <Text type="sm" color="grey35" data-cy={'price-vat-incl'}>
              {t('common.price.or_vat_include_price', 'or {{vat_include_price}} VAT. Incl', {
                vat_include_price: textFormatter.formatCurrency(clientPriceVatIncluded, priceData?.currency ?? ''),
              })}
            </Text>
          )}
        </Flex>
      );
    };
    if (useCompactView) {
      return (
        <Popover
          content={
            <SFlex direction={'column'}>
              <Text type="sm">{getDiscountText(t, priceData.garageView?.discountRate)}</Text>
              {referenceDiscount && (
                <Text type="sm" displayStyle={getDiscountTextStyle(referenceDiscount)}>
                  {`${referenceDiscount.discount}% ${t('common.discount.additional_discount', 'Additional discount')}`}
                </Text>
              )}
              <Text type="sm">
                {getVatExcludedText(
                  t,
                  calculatePriceAfterDiscount(referenceDiscount, priceData, false),
                  priceData?.currency,
                )}
              </Text>
            </SFlex>
          }
          arrowColor={theme.color.white}
          placement={'top'}
        >
          <Flex>
            {renderPrice()}
            {referenceDiscount && (
              <Icon IconComponent={TagsIcon} size={16} mt={6} ml={10} color={getDiscountColor(referenceDiscount)} />
            )}
          </Flex>
        </Popover>
      );
    } else {
      return renderPrice();
    }
  }, [
    clientPriceVatExcluded,
    clientPriceVatIncluded,
    isMKTP,
    sparePartsView,
    isTire,
    priceData,
    recommendedPriceVatExcluded,
    recommendedPriceVatIncluded,
    referenceDiscount,
    t,
    useCompactView,
    doesRefHaveMultiplePrices,
  ]);

  const renderGaragePriceView = useCallback(
    () => (
      <Flex
        minHeight={align === 'left' ? 0 : 86}
        size={1}
        direction="column"
        justify={useCompactView ? 'center' : 'flex-start'}
      >
        {renderGaragePrice()}
        {!useCompactView && (
          <>
            {hideDiscountSection ? (
              <MarginBox mt={40} />
            ) : (
              <>
                <Text type="sm" color="grey35" data-cy="margin-vat-excl">
                  {t('common.price.vat_exclude_margin', '{{vat_exclude_margin}} VAT. Excl Margin', {
                    vat_exclude_margin: textFormatter.formatCurrency(vatExcludedMargin, priceData?.currency),
                  })}
                </Text>
                <Text type="sm" color="grey35">
                  {getDiscountText(t, priceData?.garageView?.discountRate)}
                </Text>
              </>
            )}
            {narrow && !referenceDiscount && <MarginBox mt={20} />}
          </>
        )}
      </Flex>
    ),

    [
      align,
      hideDiscountSection,
      narrow,
      priceData?.currency,
      priceData?.garageView?.discountRate,
      referenceDiscount,
      renderGaragePrice,
      t,
      useCompactView,
      vatExcludedMargin,
    ],
  );
  const renderClientPriceView = useCallback(
    () => (
      <Flex minHeight={align === 'left' ? 0 : 86} size={1} direction="column" justify="flex-start">
        {renderClientPrice()}
        {!useCompactView && !isMKTP && (
          <>
            <ClientDiscountView
              displayDetailedView={displayDetailedView}
              setDisplayDetailedView={setDisplayDetailedView}
              priceData={priceData}
              discount={referenceDiscount}
              vatExcludedMargin={vatExcludedMargin}
              isAvailableSoon={isAvailableSoon}
              isNoLongerAvailable={isNoLongerAvailable}
              referenceType={referenceType}
            />
            {narrow && !referenceDiscount && <MarginBox mt={20} />}
          </>
        )}
      </Flex>
    ),
    [
      align,
      displayDetailedView,
      isAvailableSoon,
      isMKTP,
      isNoLongerAvailable,
      narrow,
      priceData,
      referenceDiscount,
      referenceType,
      renderClientPrice,
      useCompactView,
      vatExcludedMargin,
    ],
  );
  return (
    <Flex size={1} direction={'column'} align={align === 'left' ? 'flex-start' : 'flex-end'}>
      {sparePartsView === ClientView && renderClientPriceView()}
      {sparePartsView === GarageView && priceData?.garageView && renderGaragePriceView()}
    </Flex>
  );
};

export default PriceSection;
